import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import sanitizeHtml from 'sanitize-html';
import { maxFileSizeBytes } from '@client/new/common/videos/videos.constants';
import { useAddPost } from '../api/useAddPost';
import { CommunityId } from '../model/community.types';
import { MAX_MEDIA_FILES, MAX_POST_CHARS } from '../model/newPost.constants';

export type NewPost = {
  text: string;
  setText: (text: string) => void;
  media: File[];
  addMedia: (files: File[]) => void;
  removeMedia: (file: File) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  submit: () => Promise<void>;
};

export const useNewPost = (
  communityId: CommunityId,
  influencerId?: string | number,
): NewPost => {
  const addPost = useAddPost(communityId, influencerId);
  const [text, setText] = useState('');
  const [media, setMedia] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addMedia = useCallback((files: File[]) => {
    setMedia((current) => {
      const withinSize = files.filter((file) => file.size <= maxFileSizeBytes);
      if (withinSize.length !== files.length) {
        toast.error('You cannot upload files bigger than 100MB');
      }

      const remaining = MAX_MEDIA_FILES - current.length;
      const accepted = withinSize.slice(0, Math.max(0, remaining));
      if (accepted.length < withinSize.length) {
        toast.error(`You can upload max. ${MAX_MEDIA_FILES} media files`);
      }

      return accepted.length > 0 ? [...current, ...accepted] : current;
    });
  }, []);

  const removeMedia = useCallback((file: File) => {
    setMedia((current) => current.filter((item) => item !== file));
  }, []);

  const canSubmit =
    (text.trim().length > 0 || media.length > 0) && !isSubmitting;

  const submit = useCallback(async () => {
    if (text.length > MAX_POST_CHARS) {
      toast.error("Posts can't be longer than 4K characters.");
      return;
    }

    setIsSubmitting(true);
    const sanitizedText = sanitizeHtml(text, {
      allowedTags: ['a'],
      disallowedTagsMode: 'escape',
    });
    const created = await addPost({ text: sanitizedText, media });
    if (created) {
      toast.success('Post added');
      setText('');
      setMedia([]);
    }
    setIsSubmitting(false);
  }, [addPost, media, text]);

  return {
    text,
    setText,
    media,
    addMedia,
    removeMedia,
    canSubmit,
    isSubmitting,
    submit,
  };
};
